import { Box, Skeleton, Typography, useTheme } from '@mui/material';

import { ApplicationStatusChip } from 'sharedComponents/Chip/StatusChip';
import {
  AutomationAnswer,
  AutomationContainer,
  AutomationDetail,
  AutomationStatusChipContainer,
  BoldText,
  ListItemContainer,
} from 'features/Automations/styles';
import { ATSAutomationListTestIds } from 'data-testids/ATS';
import { AutomationInfoProps, AutomationListItemProps } from 'features/Automations/types';
import { useQueryGetApplicationStatusListData } from 'features/Automations/api/createQueries';
import {
  useQueryGetSuperAndDistrictAdminsData,
  useQueryGetEmailTemplateListData,
} from 'features/Automations/api/listQueries';
import auth from 'utils/auth';
import { AutomationListVariant } from 'features/Automations/enums';

export const AutomationListItemSkeleton = () => {
  const theme = useTheme();

  return (
    <>
      <AutomationContainer data-testid={ATSAutomationListTestIds.LIST_SKELETON}>
        <Skeleton sx={{ fontSize: theme.typography.body2.fontSize }} />
        <Skeleton sx={{ fontSize: theme.typography.body2.fontSize }} />
      </AutomationContainer>
    </>
  );
};

export const AutomationInfo: React.FC<AutomationInfoProps> = ({
  answer,
  email,
  status,
  variant,
  automationType,
  triggerStatus,
}) => {
  const isEditable = variant === AutomationListVariant.EDITABLE;
  const isReadOnly = variant === AutomationListVariant.READ_ONLY;

  const user = auth.getUser();
  const districtId = user?.profile?.district?.id;
  const { data: superAndDistrictAdmins } = useQueryGetSuperAndDistrictAdminsData(districtId);
  const { data: emailTemplates } = useQueryGetEmailTemplateListData(user.id);

  const getSenderName = (userId: number) => {
    const sender = superAndDistrictAdmins?.find((admin) => admin.id === Number(userId));
    return sender?.name;
  };

  const getEmailTemplateName = (templateId: number) => {
    const template = emailTemplates?.find((template) => template.id === Number(templateId));
    return template?.title;
  };

  const isQuestionAnswerAutomation = automationType === 'question_answer_update_status';
  const isReferenceCheckAutomation = automationType === 'status_update_reference_check';

  return (
    <AutomationDetail>
      {isQuestionAnswerAutomation && answer && (
        <Box sx={{ display: 'ruby' }}>
          &gt;
          <Typography sx={{ px: 0.5 }}>If a candidate responds </Typography>
          <AutomationAnswer data-testid={ATSAutomationListTestIds.CANDIDATE_ANSWER}>
            {answer.charAt(0).toUpperCase() + answer.slice(1)}
          </AutomationAnswer>
          <Typography sx={{ px: 0.5 }}>then update their status to</Typography>
          {isEditable && status && (
            <AutomationStatusChipContainer>
              <ApplicationStatusChip
                label={status.label}
                color={status.color}
                dataTestId={ATSAutomationListTestIds.NEW_STATUS_CHIP}
              />
            </AutomationStatusChipContainer>
          )}
          {isReadOnly && status && <AutomationAnswer>{status.label}</AutomationAnswer>}
        </Box>
      )}
      {isReferenceCheckAutomation && triggerStatus && (
        <Box sx={{ display: 'ruby' }}>
          &gt;
          <Typography sx={{ px: 0.5 }}>When a candidate reaches status </Typography>
          {isEditable && (
            <AutomationStatusChipContainer>
              <ApplicationStatusChip
                label={triggerStatus.label}
                color={triggerStatus.color}
                dataTestId={ATSAutomationListTestIds.NEW_STATUS_CHIP}
              />
            </AutomationStatusChipContainer>
          )}
          {isReadOnly && <AutomationAnswer>{triggerStatus.label}</AutomationAnswer>}
          <Typography sx={{ px: 0.5 }}>then send reference check</Typography>
        </Box>
      )}
      {email?.sender && email?.email_template_id && (
        <Box sx={{ display: 'ruby' }}>
          <Typography sx={{ pr: 0.25 }}> &gt; Send </Typography>
          <Typography sx={{ pr: 0.25 }}>{getEmailTemplateName(email.email_template_id)}</Typography>
          <Typography sx={{ pr: 0.25 }}>from</Typography>
          <Typography sx={{ pl: 0.25 }}>{getSenderName(email.sender)}</Typography> automatically 24
          hours after status change
        </Box>
      )}
    </AutomationDetail>
  );
};

export const AutomationListItem: React.FC<AutomationListItemProps & { localIndex?: number }> = ({
  automation,
  variant,
  localIndex,
}) => {
  const theme = useTheme();
  const districtId: number | undefined = auth.getUser()?.profile?.district.id;
  const { data: applicationStatuses, isLoading } = useQueryGetApplicationStatusListData(districtId);

  const isQuestionAnswerAutomation =
    automation.trigger.trigger_type === 'question_answer' &&
    automation.action.action_type === 'update_status';
  const isReferenceCheckAutomation =
    automation.trigger.trigger_type === 'update_status' &&
    automation.action.action_type === 'send_reference_check';

  const selectedApplicationStatus = isQuestionAnswerAutomation
    ? applicationStatuses?.find((status) => status.id === automation.action.new_status_id)
    : undefined;

  const triggerApplicationStatus = isReferenceCheckAutomation
    ? applicationStatuses?.find((status) => status.id === automation.trigger.new_status_id)
    : undefined;

  const automationType = isQuestionAnswerAutomation
    ? 'question_answer_update_status'
    : isReferenceCheckAutomation
      ? 'status_update_reference_check'
      : undefined;

  return (
    <ListItemContainer listVariant={variant}>
      <BoldText>
        Automation {localIndex !== undefined ? localIndex + 1 : automation.order + 1}:
      </BoldText>
      {isLoading && !applicationStatuses ? (
        <Skeleton sx={{ fontSize: theme.typography.body2.fontSize }} />
      ) : (
        <AutomationInfo
          answer={automation.trigger.answer}
          status={selectedApplicationStatus}
          variant={variant}
          email={automation.email}
          automationType={automationType}
          triggerStatus={triggerApplicationStatus}
        />
      )}
    </ListItemContainer>
  );
};
